<template>
    <div class="relative bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-4 flex flex-col">
        <!-- Debug: Log creator data to see what fields are available -->
        <!-- <div
            v-if="creator.banner_image_url"
            :style="{
                'background-image': `url(${creator.banner_image_url})`
            }"
            class="h-36 w-full flex absolute top-0 left-0 bg-cover bg-center from-transparent to-white"
        >
        </div> -->

        <!-- Header with Creator Info and Score -->
        <div class="flex items-start justify-between mb-4">
            <div class="flex items-start flex-1 min-w-0">
                <div
                    class="flex items-start min-w-0 hover:text-blue-600 transition-colors space-x-3"
                >
                    <!-- Large Avatar -->
                    <div class="flex-shrink-0 relative">
                        <Avatar
                            class="bg-slate-700 text-slate-50"
                            :srcs="avatarSources"
                            :alt="`Avatar for ${creator.name}`"
                            :size="64"
                        />
                    </div>

                    <!-- Creator Name and Basic Info -->
                    <div class="flex-1 min-w-0">
                        <h3 class="text-lg font-semibold text-gray-900 truncate">
                            {{ creator.display_name || creator.name }}
                        </h3>
                        <div class="text-sm text-gray-600 mb-2 leading-none">
                            @{{ creator.name }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Affinity Score -->
            <div class="flex-shrink-0 ml-3">
                <AffinityPlate :affinity-percent="relevanceScore / 10" />
            </div>
        </div>

        <!-- Platform Icons -->
        <div v-if="platformsToShow.length > 0" class="flex gap-2 mb-3">
            <AppIcon
                v-for="platform in platformsToShow"
                :key="platform.platform_id"
                :name="getPlatformIcon(platform.platform_id)"
                class="text-lg"
                :class="getPlatformIconColor(platform.platform_id)"
            />
        </div>

        <!-- Key Stats -->
        <div class="flex gap-6 mb-3 text-sm grid grid-cols-3 mt-3">
            <div v-if="totalFollowers" class="flex flex-col gap-1">
                <div class="text-gray-400 text-xs">Followers</div>
                <span class="text-gray-600 leading-none font-medium">{{ formatNumber(totalFollowers) }}</span>
            </div>
            <div v-if="creator?.residence?.country_code" class="flex flex-col gap-1">
                <div class="text-gray-400 text-xs">Country</div>
                <span class="text-gray-600 leading-none font-medium">{{ getCountryFromCode(creator.residence.country_code) }}</span>
            </div>
            <div v-if="creator?.primary_language" class="flex flex-col gap-1">
                <div class="text-gray-400 text-xs">Language</div>
                <span class="text-gray-600 leading-none font-medium">{{ getLanguageFromCode(creator?.primary_language) }}</span>
            </div>
        </div>

        <!-- Reasoning Section -->
        <div v-if="reasoningData" class="border-t border-gray-100 pt-3 grow">
            <!-- Recommendation -->
            <div v-if="reasoningData.recommendation" class="mb-2 border-b border-gray-100 pb-3">
                <h4 class="text-sm font-medium text-gray-700 mb-1">Recommendation</h4>
                <p class="text-sm text-gray-600">{{ reasoningData.recommendation }}</p>
            </div>

            <!-- Match Reasons -->
            <div v-if="reasoningData.match_reasons && reasoningData.match_reasons.length > 0" class="mb-2">
                <h4 class="text-sm font-medium text-gray-700 mb-1">Match Reasons</h4>
                <ul class="text-sm text-gray-600 space-y-1">
                    <li
                        v-for="reason in reasoningData.match_reasons"
                        :key="reason"
                        class="flex items-start"
                    >
                        <span class="text-green-500 mr-2 flex-shrink-0 mt-0.5">•</span>
                        <span>{{ reason }}</span>
                    </li>
                </ul>
            </div>

            <!-- Content Evidence -->
            <!-- <div v-if="reasoningData.content_evidence && reasoningData.content_evidence.length > 0" class="mb-2">
                <h4 class="text-sm font-medium text-gray-700 mb-1">Content Evidence</h4>
                <ul class="text-sm text-gray-600 space-y-1">
                    <li
                        v-for="evidence in reasoningData.content_evidence"
                        :key="evidence"
                        class="flex items-start"
                    >
                        <span class="text-blue-500 mr-2 flex-shrink-0 mt-0.5">•</span>
                        <span class="italic">{{ evidence }}</span>
                    </li>
                </ul>
            </div> -->

            <!-- Rejection Reason (for rejected creators) -->
            <div v-if="reasoningData.rejection_reason && reasoningData.rejection_reason !== 'none'" class="mb-2">
                <h4 class="text-sm font-medium text-red-700 mb-1">Rejection Reason</h4>
                <p class="text-sm text-red-600 bg-red-50 px-2 py-1 rounded">{{ reasoningData.rejection_reason }}</p>
            </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-2 pt-3 border-t border-gray-100 mt-auto">
            <router-link
                :to="{
                    path: $route.path,
                    query: {
                        ...$route.query,
                        modal: 'creator',
                        platform_id: primaryPlatform,
                        platform_user_id: creatorId,
                    },
                }"
                class="ml-auto flex min-w-0 hover:text-blue-600 transition-colors space-x-3"
            >
                <button v-if="isLegacyBilling" class="flex-1 bg-indigo-500 text-white text-sm px-3 py-1.5 rounded hover:bg-indigo-600 transition-all duration-300 flex items-center justify-center gap-2 group">
                    <div class="relative w-4 h-4 flex items-center justify-center">
                        <AppIcon
                            name="rectangle-list"
                            class="text-xs"
                        />
                    </div>
                    Open
                </button>

                <button v-else class="flex-1 bg-indigo-500 text-white text-sm px-3 py-1.5 rounded hover:bg-indigo-600 transition-all duration-300 flex items-center justify-center gap-2 group">
                    <div class="relative w-4 h-4 flex items-center justify-center">
                        <AppIcon
                            name="lock"
                            class="text-xs transition-all duration-300 group-hover:opacity-0 group-hover:scale-75"
                        />
                        <AppIcon
                            name="unlock"
                            class="text-xs absolute transition-all duration-300 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100"
                        />
                    </div>
                    Unlock
                </button>
            </router-link>
        </div>
    </div>
</template>

<script>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import AffinityPlate from '@/components/plates/AffinityPlate.vue'
import Avatar from '@/components/Avatar.vue'
import { formatThousands } from '@/utils/Utils'
import AppIcon from '@/components/AppIcon.vue'

export default {
    name: 'CreatorCard',
    components: {
        AffinityPlate,
        Avatar,
        AppIcon,
    },
    props: {
        creator: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapGetters(['getLanguageFromCode', 'getCountryFromCode', 'isLegacyBilling']),

        creatorId() {
            // Handle database search schema
            return this.creator.source_ids?.[0]?.id || this.creator._id
        },
        primaryPlatform() {
            // Handle database search schema
            return this.creator.source_ids?.[0]?.platform_id || this.creator.platform_id || 'unknown'
        },
        creatorName() {
            // Handle database search schema
            return this.creator.normalized?.display_name ||
                   this.creator.display_name ||
                   this.creator.normalized?.name ||
                   this.creator.name ||
                   'Unknown Creator'
        },
        avatarSources() {
            return _.map(this.creator.source_ids, ({ id, platform_id }) => {
                return `${MEDIA_CDN_BASE}/${platform_id}/avatars/${
                    'large'
                }/${id}`
            })
        },
        relevanceScore() {
            return this.creator.reasoning?.relevance_score ||
                   this.creator.relevance_score ||
                   this.creator.ai_analysis?.relevance_score ||
                   this.creator.match_score ||
                   0
        },
        reasoningData() {
            return this.creator.reasoning ||
                   this.creator.ai_analysis ||
                   this.creator.match_reasoning ||
                   null
        },
        platformsToShow() {
            // Handle database search schema
            if (this.creator.source_ids && this.creator.source_ids.length > 0) {
                return this.$lodash.uniqBy(this.creator.source_ids, 'platform_id')
            } else if (this.creator.platform_id) {
                return [{ platform_id: this.creator.platform_id }]
            } else if (this.creator.normalized?.socials) {
                return Object.values(this.creator.normalized.socials).map(social => ({
                    platform_id: social.platform_id
                }))
            }
            return []
        },
        totalFollowers() {
            // Handle database search schema
            return this.creator.total_followers || this.creator.normalized?.followers || null
        },
        engagementRate() {
            // Handle database search schema
            return this.creator.engagement_rate || this.creator.normalized?.engagement_rate || null
        },
        hasAdditionalStats() {
            return this.creator.avg_views ||
                   this.creator.subscriber_count ||
                   this.creator.video_count ||
                   (this.creator.location && !this.getCountryFromCreator(this.creator))
        },
    },
    methods: {
        formatNumber(number) {
            return formatThousands(number, 'compact')
        },

        getPlatformIcon(platformId) {
            const iconMap = {
                'twitch': 'twitch',
                'youtube': 'youtube',
                'instagram': 'instagram',
                'tiktok': 'tiktok',
                'twitter': 'x-twitter',
                'x': 'x-twitter',
                'facebook': 'facebook',
                'discord': 'discord',
                'snapchat': 'snapchat',
                'reddit': 'reddit'
            }
            return iconMap[platformId?.toLowerCase()] || 'globe'
        },

        getPlatformIconColor(platformId) {
            const colorMap = {
                'twitch': 'text-purple-600',
                'youtube': 'text-red-600',
                'instagram': 'text-pink-600',
                'tiktok': 'text-black',
                'twitter': 'text-x',
                'x': 'text-black',
                'facebook': 'text-blue-700',
                'discord': 'text-indigo-600',
                'snapchat': 'text-yellow-400',
                'reddit': 'text-orange-600'
            }
            return colorMap[platformId?.toLowerCase()] || 'text-gray-600'
        },

        getCountryFromCreator(creator) {
            // Handle database search schema
            return creator.residence.country_code
        },

        openAddToModal() {
            // Convert creator data to superview format expected by the modal
            const superview = {
                _id: this.creatorId,
                normalized: {
                    name: this.creator.name || this.creator.display_name,
                    display_name: this.creator.display_name || this.creator.name,
                    avatar_url: this.creator.avatar_url || this.creator.profile_image_url || this.creator.banner_image_url
                },
                platform_id: this.primaryPlatform
            }

            this.$store.commit('modal:setData', {
                modal_id: 'add_creator_to_modal',
                data: {
                    is_open: true,
                    platform_id: this.primaryPlatform,
                    superviews: [superview], // Array with single creator
                },
            })
        },
    },
}
</script>
